import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import "./NavBar.css";
import { Menu, Dropdown, Avatar, Badge, Drawer } from "antd";
import AntIcon from "@ant-design/icons";

import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";

import { ReactComponent as MailboxIcon } from "assets/icons/my_mailbox.svg";
import { ReactComponent as ProfileIcon } from "assets/icons/my_profile.svg";
import { ReactComponent as SettingIcon } from "assets/icons/my_settings.svg";
import { ReactComponent as BillingIcon } from "assets/icons/my_billing.svg";
import { ReactComponent as CertificateIcon } from "assets/icons/my_certificates.svg";
import { ReactComponent as SignoutIcon } from "assets/icons/sign_out.svg";
import { ReactComponent as GoToChinaIcon } from "assets/icons/go_to_china.svg";
import { ReactComponent as HelpIcon } from "assets/icons/help.svg";
import { ReactComponent as GiftIcon } from "assets/icons/gift-solid.svg";
import { ReactComponent as ForumIcon } from "assets/icons/forum.svg";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { MenuInfo as ClickParam } from "rc-menu/lib/interface";
import GET_NAVBAR_INFO from "graphql/queries/GetNavbarInfo";
import GET_LANGUAGE_DETAILS from "graphql/queries/GetLanguageDetails";
import GET_LANGUAGE_DATA_CACHE from "graphql/queries/GetLanguageDataCache";
import GET_FORUM_NOTIFICATIONS from "graphql/queries/GetForumNotificationCount";
import logo from "assets/flexi-logo.png";

import { languageDataVar } from "cache";

const Icon = styled(AntIcon)`
  svg {
    width: 2em;
    height: 2em;
  }
`;

const StudyLanguage = styled.span`
  position: absolute;
  height: 14px;
  bottom: -7px;
  background-color: #1070b6;
  color: white;
  font-size: 8px;
  font-weight: 700;
  margin: 0px 0px 4px 8px;
  padding: 3px;
  border-radius: 2px;
  text-align: center;
  white-space: nowrap;
`;

const { SubMenu } = Menu;

function NavBar(props: any) {
  const { logOut } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation("navbar");
  const forumUrl =
    process.env.REACT_APP_DISCOURSE_FORUM_URL ||
    `${window.location.origin}/forum`;

  // TODO: use Fragment to get avatar field only
  const { data } = useQuery(GET_NAVBAR_INFO);
  const { data: dataForum } = useQuery(GET_FORUM_NOTIFICATIONS);
  const { data: { languageDataCache: { studyLanguageId = "1" } = {} } = {} } =
    useQuery(GET_LANGUAGE_DATA_CACHE);

  const { data: languageDetails, refetch: refetchLanguageDetails } = useQuery(
    GET_LANGUAGE_DETAILS,
    {
      variables: {
        id: languageDataVar().studyLanguageId,
      },
    }
  );

  useEffect(() => {
    refetchLanguageDetails({ id: studyLanguageId });
  }, [studyLanguageId, refetchLanguageDetails]);

  const { unreadForumNotificationCount } = dataForum?.me || {};

  const { languageDetails: { realName = "" } = {} } = languageDetails || {};

  const { givenName, familyName, image, unreadNotificationsCount } =
    data?.me || {};

  const fullName = `${givenName || ""} ${familyName || ""}`;

  const { totalAmount: creditAmount } = data?.myCredits || {};

  const [route, setRoute] = useState("");

  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    // get rid of the "/" in the pathname using slice
    const pathname: string = location.pathname.split("/")[1];
    // update route state
    setRoute(pathname !== "" ? pathname : "register");
    // Update metadata title
    // The convention here is that pathname will be kebab-case (i.e. my-classes)
    // Therefore first get individual parts of the phrase then capitalize 1st letter of each word
    if (pathname !== "") {
      const words = pathname.split("-");
      let title = "LTL Flexi Classes - ";
      words.forEach((word: string) => {
        title += `${word[0].toUpperCase() + word.slice(1)} `;
      });
      document.title = title;
    }
  }, [location.pathname]);

  const isMobile = (): boolean =>
    !window.matchMedia("(min-width: 800px)").matches;

  const onProfileTab = (params: ClickParam) => {
    const newRoute = params.key;
    if (newRoute === "sign-out") {
      logOut();
      navigate("/login");
    } else if (newRoute === "forum") {
      window.open(forumUrl, "_blank");
    } else if (newRoute === "go-to-china") {
      window.open(t("GOTO_CHINA_PAGE_URL"), "_blank");
    } else if (newRoute === "help") {
      window.open(t("HELP_PAGE_URL"), "_blank");
    } else {
      navigate(`/account/${newRoute}`);
    }
  };

  const onNavBarMenu = (params: ClickParam) => {
    const { key, keyPath } = params;
    const newRoute = key;
    /* Every menu tab will have a keyPath property, which is an array.
      The first element of this array will be the key of the tab itself.
      The second element of this array (if it exists) is the key of the parent tab
      (in the case of using a submenu)
    */
    // if it's the normal menu
    if (keyPath.length === 1) {
      if (newRoute === "account") {
        // do nothing when click on profile item
      }
      // else if it's the submenu in the mobile nav
    } else if (keyPath.length === 2) {
      // right now do nothing
      onProfileTab(params);
    }

    // if on mobile, close the drawer
    if (isMobile()) {
      setDrawerVisible(false);
    }
  };

  const sharedNavbarItems = (() => {
    const navbarItems = [
      {
        path: "/my-classes",
        text: t("My classes").toUpperCase(),
      },
      {
        path: "/book-class",
        text: t("BOOK A CLASS"),
      },
      {
        path: "/new-book-course",
        text: t("FULL COURSES"),
      },
      {
        path: "/my-level",
        text: t("MY LEVEL"),
      },
      {
        path: "/my-credits",
        text: `${t("CREDITS")}: ${creditAmount || 0}`,
      },
      {
        path: "/forum",
        text: t("FORUM"),
        noti: unreadForumNotificationCount,
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          window.open(process.env.REACT_APP_FORUM_URL, "_blank");
        },
      },
    ];
    return navbarItems.map((item) => (
      <Menu.Item className="nav-tab" key={item.path.split("/")[1]}>
        <NavLink
          onClick={item.onClick}
          style={{ color: "inherit" }}
          to={item.path}
        >
          {item.text}
        </NavLink>
        {item.noti ? (
          <Badge
            className="forum-bubble"
            count={item.noti}
            size="small"
            offset={isMobile() ? [-6, -16] : [-7, -19]}
          />
        ) : null}
      </Menu.Item>
    ));
  })();

  const profileTabItems = [
    <Menu.Item
      className="dropdown-menu-item"
      key="my-mailbox"
      icon={<Icon component={MailboxIcon} />}
    >
      {t("MY MAILBOX")}
      <Badge
        count={unreadNotificationsCount}
        style={{
          backgroundColor: isMobile() ? "#0f70b6" : "#fdd440",
          color: "#00538b",
          marginLeft: "12px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          letterSpacing: "0px",
        }}
      />
    </Menu.Item>,
    <Menu.Item
      key="refer-a-friend"
      className="dropdown-menu-item"
      icon={<Icon component={GiftIcon} />}
    >
      {t("REFER A FRIEND")}
    </Menu.Item>,
    <Menu.Item
      key="my-profile"
      className="dropdown-menu-item"
      icon={<Icon component={ProfileIcon} />}
    >
      {t("MY PROFILE")}
    </Menu.Item>,
    <Menu.Item
      key="forum"
      className="dropdown-menu-item"
      icon={<Icon component={ForumIcon} />}
    >
      {t("DISCORD_FORUM")}
    </Menu.Item>,
    <Menu.Item
      key="go-to-china"
      className="dropdown-menu-item"
      icon={<Icon component={GoToChinaIcon} />}
    >
      {t("GO TO CHINA")}
    </Menu.Item>,
    <Menu.Item
      key="my-settings"
      className="dropdown-menu-item"
      icon={<Icon component={SettingIcon} />}
    >
      {t("MY SETTINGS")}
    </Menu.Item>,
    <Menu.Item
      key="my-billings"
      className="dropdown-menu-item"
      icon={<Icon component={BillingIcon} />}
    >
      {t("MY BILLING")}
    </Menu.Item>,
    <Menu.Item
      key="my-certificates"
      className="dropdown-menu-item"
      icon={<Icon component={CertificateIcon} />}
    >
      {t("MY CERTIFICATES")}
    </Menu.Item>,
    <Menu.Item
      key="help"
      className="dropdown-menu-item"
      icon={<Icon component={HelpIcon} />}
    >
      {t("HELP")}
    </Menu.Item>,
    <Menu.Item
      key="sign-out"
      className="dropdown-menu-item"
      icon={<Icon component={SignoutIcon} />}
    >
      {t("SIGN OUT")}
    </Menu.Item>,
  ];
  const profileTab = (
    <Menu className="profile-dropdown" onClick={onProfileTab}>
      {profileTabItems}
    </Menu>
  );

  const renderAvatar = () => {
    if (!image) {
      return (
        <Avatar
          className="avatar"
          size="large"
          style={{
            backgroundColor: "#d3e8ff",
            color: "#2b77af",
            verticalAlign: "middle",
          }}
          src={image}
        >
          {fullName[0]}
        </Avatar>
      );
    }
    return (
      <Avatar
        className="avatar"
        size="large"
        style={{
          verticalAlign: "middle",
        }}
        src={image}
      />
    );
  };

  const renderDesktopNavbarMenu = () => {
    const navbarItems = [
      ...sharedNavbarItems,
      <Menu.Item
        className="nav-tab no-underline account-nav-item"
        key="account"
        id="account-nav-tab"
      >
        <div id="header-dropdown" className="header-dropdown">
          <Dropdown
            overlay={profileTab}
            placement="bottomCenter"
            getPopupContainer={() =>
              document.getElementById("header-dropdown") as HTMLElement
            }
          >
            <div className="nav-tab profile">
              <Badge offset={[-7, 7]}>
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {renderAvatar()}
                  <StudyLanguage>{realName} </StudyLanguage>
                </div>
              </Badge>
            </div>
          </Dropdown>
        </div>
      </Menu.Item>,
    ];
    return (
      <Menu
        className="navbar"
        id="navbar"
        theme="light"
        mode="horizontal"
        onClick={onNavBarMenu}
        selectedKeys={[route]}
      >
        {navbarItems}
      </Menu>
    );
  };

  const renderMobileNavbarMenu = () => {
    const UserAvatar = (
      <div className="nav-tab profile">
        {fullName}
        <Badge offset={[-7, 7]}>
          <div className="avatar-container">
            {renderAvatar()}
            <StudyLanguage>{realName} </StudyLanguage>
          </div>
        </Badge>
      </div>
    );
    const navbarItems = [
      ...sharedNavbarItems,
      <SubMenu key="user-profile" title={fullName} icon={UserAvatar}>
        {profileTabItems}
      </SubMenu>,
    ];

    return (
      <Drawer
        placement="left"
        closable={false}
        onClose={() => {
          setDrawerVisible(false);
        }}
        visible={drawerVisible}
      >
        <Menu
          className="mobile-navbar"
          id="navbar"
          theme="light"
          mode="inline"
          onClick={onNavBarMenu}
          selectedKeys={[route]}
        >
          {navbarItems}
        </Menu>
      </Drawer>
    );
  };

  const handleLogoClick = (e: any) => {
    e.preventDefault();
    navigate(`/my-classes/`);
  };

  const renderLogo = () => (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a onClick={handleLogoClick} onKeyDown={handleLogoClick} href="#">
      <img className="logo" src={logo} alt="ltl-logo" />
    </a>
  );

  const renderNavbar = () => {
    // if already signed in, 2 cases
    // for desktop:
    if (!isMobile()) {
      return (
        <>
          {renderLogo()}
          {renderDesktopNavbarMenu()}
        </>
      );
    }
    // for mobile
    return (
      <>
        <div className="icon-logo-container">
          <div className="menu-icon-container">
            <FontAwesomeIcon
              icon={faBars as IconProp}
              className="menu-icon"
              size="2x"
              onClick={() => {
                setDrawerVisible(true);
              }}
            />
          </div>
          {renderLogo()}
        </div>
        {renderMobileNavbarMenu()}
      </>
    );
  };

  return renderNavbar();
}

export default NavBar;
